<template>
  <div class="actividades-nueva">
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">Nueva Actividad</h1>
        <p class="content-header-subtitle">Crear una nueva actividad</p>
      </div>
      <div class="content-header-right">
        <b-button variant="outline-secondary" @click="volver">
          <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
          Volver
        </b-button>
      </div>
    </div>

    <b-card>
      <div v-if="cargando" class="text-center py-4">
        <b-spinner variant="primary" label="Cargando..."></b-spinner>
        <p class="mt-2">Cargando datos del formulario...</p>
      </div>
      
      <b-form v-else @submit.prevent="guardarActividad">
        <b-row>
          <b-col md="6">
            <b-form-group label="Tipo de Actividad" required>
              <b-form-select 
                v-model="actividad.tipoActividad" 
                :options="opcionesTipoActividad" 
                :state="validacion.tipoActividad"
                @change="validacion.tipoActividad = actividad.tipoActividad ? true : null"
                required 
              />
              <b-form-invalid-feedback v-if="validacion.tipoActividad === false">
                Este campo es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Fecha" required>
              <b-form-datepicker 
                v-model="actividad.fecha" 
                :start-weekday="1"
                :state="validacion.fecha"
                @input="validacion.fecha = actividad.fecha ? true : null"
                required 
              />
              <b-form-invalid-feedback v-if="validacion.fecha === false">
                Este campo es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Estado de Actividad" required>
              <b-form-select 
                v-model="actividad.estadoActividad" 
                :options="opcionesEstadoActividad" 
                :state="validacion.estadoActividad"
                @change="validacion.estadoActividad = actividad.estadoActividad ? true : null"
                required 
              />
              <b-form-invalid-feedback v-if="validacion.estadoActividad === false">
                Este campo es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Proyecto">
              <b-form-select v-model="actividad.proyecto" :options="opcionesProyecto" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Próxima Acción Fecha" required>
              <b-form-datepicker 
                v-model="actividad.proximaAccionFecha" 
                :start-weekday="1"
                :state="validacion.proximaAccionFecha"
                @input="validacion.proximaAccionFecha = actividad.proximaAccionFecha ? true : null"
                required 
              />
              <b-form-invalid-feedback v-if="validacion.proximaAccionFecha === false">
                Este campo es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Próxima Acción Tipo" required>
              <b-form-select 
                v-model="actividad.proximaAccionTipo" 
                :options="opcionesAccionActividad" 
                :state="validacion.proximaAccionTipo"
                @change="validacion.proximaAccionTipo = actividad.proximaAccionTipo ? true : null"
                required 
              />
              <b-form-invalid-feedback v-if="validacion.proximaAccionTipo === false">
                Este campo es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Recordatorio">
              <b-form-checkbox v-model="actividad.recordatorio" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Observaciones">
          <b-form-textarea v-model="actividad.observaciones" rows="4" />
        </b-form-group>

        <div class="d-flex justify-content-end gap-2">
          <b-button variant="outline-secondary" @click="volver">Cancelar</b-button>
          <b-button type="submit" variant="primary" :disabled="guardando">
            {{ guardando ? 'Guardando...' : 'Guardar Actividad' }}
          </b-button>
        </div>
      </b-form>

      <!-- Subida de documentos (se muestra después de crear la actividad) -->
      <div v-if="mostrarSubidaDocumentos && actividadCreada" class="mt-4">
        <b-card title="Adjuntar Documentos (Opcional)" class="mb-4">
          <p class="text-muted mb-3">
            La actividad ha sido creada exitosamente. Puedes adjuntar documentos opcionalmente.
          </p>
          
          <SubidaDocumentos 
            :id-actividad="actividadCreada.idActividad"
            :id-usuario="usuarioActual.idUsuario"
            @documento-subido="onDocumentoSubido"
            @finalizar-subida="onFinalizarSubida"
          />
          
          <div class="d-flex justify-content-end gap-2 mt-3">
            <b-button variant="outline-secondary" @click="finalizarSinDocumentos">
              Finalizar sin documentos
            </b-button>
            <b-button variant="primary" @click="irADetalleActividad">
              Ir al detalle de la actividad
            </b-button>
          </div>
        </b-card>
      </div>
    </b-card>

  </div>
</template>

<script>
import { TipoActividadService } from '@/services/tipoActividadService'
import { EstadoActividadService } from '@/services/estadoActividadService'
import { AccionActividadService } from '@/services/accionActividadService'
import { ProyectoService } from '@/services/proyectoService'
import { ActividadService } from '@/services/actividadService'
import { getUserData } from '@/auth/utils'
import SubidaDocumentos from '@/components/SubidaDocumentos.vue'
import { 
  generarEnlaceGoogleCalendar, 
  generarEnlaceOutlookCalendar, 
  descargarArchivoICS,
  crearEventoDesdeActividad,
  generarArchivoICS
} from '@/utils/calendarUtils'

export default {
  name: 'ActividadesNueva',
  components: {
    SubidaDocumentos
  },
  data() {
    return {
      guardando: false,
      cargando: true,
      actividad: {
        tipoActividad: null,
        fecha: '',
        estadoActividad: null,
        observaciones: '',
        proximaAccionFecha: '',
        proximaAccionTipo: null,
        recordatorio: false,
        proyecto: null,
      },
      actividadCreada: null,
      mostrarSubidaDocumentos: false,
      usuarioActual: {
        idUsuario: 1 // TODO: Obtener del store de autenticación
      },
      validacion: {
        tipoActividad: null,
        fecha: null,
        estadoActividad: null,
        proximaAccionFecha: null,
        proximaAccionTipo: null,
      },
      opcionesTipoActividad: [
        { value: null, text: 'Seleccionar tipo de actividad' }
      ],
      opcionesEstadoActividad: [
        { value: null, text: 'Seleccionar estado' }
      ],
      opcionesAccionActividad: [
        { value: null, text: 'Seleccionar acción' }
      ],
      opcionesProyecto: [
        { value: null, text: 'Seleccionar proyecto (opcional)' }
      ],
    }
  },
  async mounted() {
    await this.cargarDatosIniciales()
  },
  methods: {
    async cargarDatosIniciales() {
      this.cargando = true
      try {
        console.log('🔄 Iniciando carga de datos iniciales...')
        
        // Cargar todos los datos en paralelo
        const [tiposActividad, estadosActividad, accionesActividad, proyectos] = await Promise.all([
          TipoActividadService.getAll(),
          EstadoActividadService.getAll(),
          AccionActividadService.getAll(),
          ProyectoService.getAll()
        ])

        console.log('📊 Datos recibidos del backend:')
        console.log('Tipos de actividad:', tiposActividad)
        console.log('Estados de actividad:', estadosActividad)
        console.log('Acciones de actividad:', accionesActividad)
        console.log('Proyectos:', proyectos)

        // Mapear tipos de actividad
        this.opcionesTipoActividad = [
          { value: null, text: 'Seleccionar tipo de actividad' },
          ...tiposActividad.map(tipo => ({
            value: tipo.idTipoActividad,
            text: tipo.nombre
          }))
        ]
        console.log('✅ Opciones tipo actividad mapeadas:', this.opcionesTipoActividad)

        // Mapear estados de actividad
        this.opcionesEstadoActividad = [
          { value: null, text: 'Seleccionar estado' },
          ...estadosActividad.map(estado => ({
            value: estado.idEstadoActividad,
            text: estado.nombre
          }))
        ]
        console.log('✅ Opciones estado actividad mapeadas:', this.opcionesEstadoActividad)

        // Mapear acciones de actividad
        this.opcionesAccionActividad = [
          { value: null, text: 'Seleccionar acción' },
          ...accionesActividad.map(accion => ({
            value: accion.idAccionActividad,
            text: accion.nombre
          }))
        ]
        console.log('✅ Opciones acción actividad mapeadas:', this.opcionesAccionActividad)

        // Mapear proyectos
        this.opcionesProyecto = [
          { value: null, text: 'Seleccionar proyecto (opcional)' },
          ...proyectos.map(proyecto => ({
            value: proyecto.idProyecto,
            text: proyecto.nombre
          }))
        ]
        console.log('✅ Opciones proyecto mapeadas:', this.opcionesProyecto)

        // Establecer fecha actual como valor por defecto
        this.actividad.fecha = new Date().toISOString().split('T')[0]
        this.actividad.proximaAccionFecha = new Date().toISOString().split('T')[0]

        console.log('🎉 Carga de datos completada exitosamente')
        console.log('Estado final de las opciones:')
        console.log('- Tipo actividad:', this.opcionesTipoActividad.length, 'opciones')
        console.log('- Estado actividad:', this.opcionesEstadoActividad.length, 'opciones')
        console.log('- Acción actividad:', this.opcionesAccionActividad.length, 'opciones')
        console.log('- Proyectos:', this.opcionesProyecto.length, 'opciones')

      } catch (error) {
        console.error('Error al cargar datos iniciales:', error)
        this.$toast({
          message: 'Error al cargar los datos del formulario',
          variant: 'danger',
        })
      } finally {
        this.cargando = false
      }
    },

    async guardarActividad() {
      // Resetear validación
      this.validacion = {
        tipoActividad: null,
        fecha: null,
        estadoActividad: null,
        proximaAccionFecha: null,
        proximaAccionTipo: null,
      }

      // Validar campos obligatorios
      let hayErrores = false

      if (!this.actividad.tipoActividad) {
        this.validacion.tipoActividad = false
        hayErrores = true
      } else {
        this.validacion.tipoActividad = true
      }

      if (!this.actividad.fecha) {
        this.validacion.fecha = false
        hayErrores = true
      } else {
        this.validacion.fecha = true
      }

      if (!this.actividad.estadoActividad) {
        this.validacion.estadoActividad = false
        hayErrores = true
      } else {
        this.validacion.estadoActividad = true
      }

      if (!this.actividad.proximaAccionFecha) {
        this.validacion.proximaAccionFecha = false
        hayErrores = true
      } else {
        this.validacion.proximaAccionFecha = true
      }

      if (!this.actividad.proximaAccionTipo) {
        this.validacion.proximaAccionTipo = false
        hayErrores = true
      } else {
        this.validacion.proximaAccionTipo = true
      }

      if (hayErrores) {
        this.$toast({
          message: 'Por favor, complete todos los campos obligatorios',
          variant: 'warning',
        })
        // Hacer scroll al primer campo con error
        this.$nextTick(() => {
          // Buscar el primer campo con error (select o input dentro de un form-group con estado inválido)
          const formGroups = document.querySelectorAll('.form-group')
          for (const group of formGroups) {
            const select = group.querySelector('select.is-invalid')
            const input = group.querySelector('input.is-invalid')
            const target = select || input
            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'center' })
              if (target.tagName === 'SELECT' || target.tagName === 'INPUT') {
                target.focus()
              }
              break
            }
          }
        })
        return
      }

      this.guardando = true
      try {
        // Si el recordatorio está marcado, mostrar diálogos de configuración
        if (this.actividad.recordatorio) {
          this.guardando = false
          await this.configurarRecordatorio()
          return
        }

        // Si no hay recordatorio, proceder directamente con el guardado
        await this.procesarGuardadoActividad()
      } catch (error) {
        console.error('Error al guardar actividad:', error)
        this.$toast({
          message: 'Error al crear la actividad',
          variant: 'danger',
        })
      } finally {
        this.guardando = false
      }
    },

    volver() {
      this.$router.push({ name: 'actividades-lista' })
    },

    onDocumentoSubido() {
      this.$toast({
        message: 'Documento subido correctamente',
        variant: 'success',
      })
      // No redirigir automáticamente, dejar que el usuario decida
    },

    onFinalizarSubida() {
      this.$toast({
        message: 'Subida de documentos finalizada',
        variant: 'info',
      })
      // Ocultar la sección de subida de documentos
      this.mostrarSubidaDocumentos = false
    },

    finalizarSinDocumentos() {
      this.$router.push({ name: 'actividades-lista' })
    },

    irADetalleActividad() {
      this.$router.push({ name: 'actividades-detalle', params: { id: this.actividadCreada.idActividad } })
    },

    // Método para configurar recordatorio usando SweetAlert2
    async configurarRecordatorio() {
      // Primero pedir la hora del recordatorio
      const result = await this.$swal({
        title: 'Configurar Recordatorio',
        html: `
          <div class="text-left">
            <p>Selecciona la hora para el recordatorio:</p>
            <div class="form-group">
              <label for="hora">Hora:</label>
              <input type="time" id="hora" class="form-control" value="09:00" required>
            </div>
            <p class="text-muted small mt-2">Fecha: ${this.formatearFecha(this.actividad.proximaAccionFecha)}</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
        didOpen: () => {
          // Asegurar que el input esté enfocado
          setTimeout(() => {
            const input = document.getElementById('hora')
            if (input) input.focus()
          }, 100)
        },
        preConfirm: () => {
          const horaInput = document.getElementById('hora')
          if (!horaInput || !horaInput.value) {
            this.$swal.showValidationMessage('Por favor selecciona una hora')
            return false
          }
          return horaInput.value
        }
      })

      if (!result.value) {
        // Si se cancela, desactivar recordatorio
        this.actividad.recordatorio = false
        return
      }
      
      const horaRecordatorio = result.value

      // Primero guardar la actividad para obtener el ID
      await this.procesarGuardadoActividad()

      // Si la actividad se guardó correctamente, proceder con el recordatorio
      if (this.actividadCreada && this.actividadCreada.idActividad) {
        // Crear el evento con la hora seleccionada usando la actividad creada
        const actividadCompleta = {
          ...this.actividad,
          idActividad: this.actividadCreada.idActividad
        }
        const evento = crearEventoDesdeActividad(actividadCompleta, horaRecordatorio)
        
        // Mostrar opciones de recordatorio directamente
        const recordatorioResult = await this.$swal({
          title: 'Agregar Recordatorio',
          text: 'Selecciona cómo quieres agregar el recordatorio:',
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Descargar archivo',
          cancelButtonText: 'Cancelar',
          showDenyButton: true,
          denyButtonText: 'Enviar por correo',
          showCloseButton: true,
          customClass: {
            confirmButton: 'btn btn-info',
            denyButton: 'btn btn-warning',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })

        if (recordatorioResult.isConfirmed) {
          descargarArchivoICS(evento)
        } else if (recordatorioResult.isDenied) {
          await this.enviarRecordatorioPorCorreo(actividadCompleta, horaRecordatorio)
        }
      }
    },

    async enviarRecordatorioPorCorreo(actividad, horaRecordatorio) {
      try {
        const result = await this.$swal({
          title: 'Enviar Recordatorio por Correo',
          html: `
            <div class="text-left">
              <p>Ingresa las direcciones de correo electrónico:</p>
              <div class="form-group">
                <label for="emails">Correos electrónicos:</label>
                <textarea 
                  id="emails" 
                  class="form-control" 
                  rows="3" 
                  placeholder="ejemplo1@correo.com, ejemplo2@correo.com, ejemplo3@correo.com"
                  required
                ></textarea>
                <small class="form-text text-muted">Separa múltiples correos con comas</small>
              </div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: 'Enviar',
          cancelButtonText: 'Cancelar',
          customClass: {
            confirmButton: 'btn btn-warning',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
          preConfirm: () => {
            const emailsInput = document.getElementById('emails')
            if (!emailsInput || !emailsInput.value.trim()) {
              this.$swal.showValidationMessage('Por favor ingresa al menos un correo electrónico')
              return false
            }
            return emailsInput.value.trim()
          }
        })

        if (result.value) {
          // Parsear los emails (separados por comas)
          const emails = result.value
            .split(',')
            .map(email => email.trim())
            .filter(email => email.length > 0)

          // Generar el evento y el archivo ICS con la hora seleccionada
          const evento = crearEventoDesdeActividad(actividad, horaRecordatorio)
          const archivoICS = generarArchivoICS(evento)
          
          // Enviar el archivo ICS por correo a múltiples destinatarios
          await this.enviarCorreoRecordatorio(actividad.idActividad, emails, archivoICS, evento)
          
          this.$swal({
            title: '¡Enviado!',
            text: `El recordatorio ha sido enviado a ${emails.length} destinatario${emails.length > 1 ? 's' : ''}`,
            icon: 'success',
            confirmButtonText: 'Aceptar',
            customClass: {
              confirmButton: 'btn btn-success',
            },
            buttonsStyling: false,
          })
        }
      } catch (error) {
        console.error('Error al enviar el correo:', error)
        this.$swal({
          title: 'Error',
          text: 'No se pudo enviar el recordatorio. Inténtalo de nuevo.',
          icon: 'error',
          confirmButtonText: 'Aceptar',
          customClass: {
            confirmButton: 'btn btn-danger',
          },
          buttonsStyling: false,
        })
      }
    },

    async enviarCorreoRecordatorio(actividadId, emails, archivoICS, evento) {
      try {
        const response = await this.$http.post('/email/recordatorio-actividad', {
          actividadId: actividadId,
          emailsDestino: emails,
          archivoICS: archivoICS,
          evento: evento
        })
        
        if (response.data.success) {
          return Promise.resolve()
        } else {
          throw new Error(response.data.message || 'Error al enviar el correo')
        }
      } catch (error) {
        console.error('Error al enviar correo:', error)
        throw new Error('Error al enviar el recordatorio por correo')
      }
    },

    formatearFecha(fecha) {
      if (!fecha) return ''
      const fechaObj = new Date(fecha)
      return fechaObj.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    async procesarGuardadoActividad() {
      this.guardando = true
      try {
        // Obtener el usuario actual
        const userData = getUserData()
        const idCreadoPor = userData?.idUsuario || 1 // Usar ID del usuario actual o 1 por defecto

        // Preparar los datos para enviar al backend
        const actividadData = {
          idTipoActividad: this.actividad.tipoActividad,
          idEstadoActividad: this.actividad.estadoActividad,
          fecha: this.actividad.fecha,
          proximaAccionFecha: this.actividad.proximaAccionFecha,
          idProximaAccionTipo: this.actividad.proximaAccionTipo,
          observaciones: this.actividad.observaciones || '',
          recordatorio: this.actividad.recordatorio,
          idCreadoPor: idCreadoPor,
          idProyecto: this.actividad.proyecto || null
        }

        console.log('📤 Enviando datos de actividad al backend:', actividadData)
        
        // Llamar al servicio para crear la actividad
        const actividadCreada = await ActividadService.create(actividadData)
        
        console.log('✅ Actividad creada exitosamente:', actividadCreada)
        
        // Guardar la actividad creada y mostrar opción de subir documentos
        this.actividadCreada = actividadCreada
        this.mostrarSubidaDocumentos = true
        
        this.$toast({
          message: 'Actividad creada correctamente. Puedes adjuntar documentos opcionalmente.',
          variant: 'success',
        })
      } catch (error) {
        console.error('Error al guardar actividad:', error)
        this.$toast({
          message: 'Error al crear la actividad',
          variant: 'danger',
        })
      } finally {
        this.guardando = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.actividades-nueva {
  padding: 1.5rem;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.gap-2 {
  gap: 0.5rem;
}
</style>

